// src/pages/markets/board/api/useSchema.ts

import { useQuery } from '@tanstack/react-query';
import { fetchBoardSchema } from './boardApi';
import { boardKeys } from './boardKeys';
import {
    GATED_FIELDS,
    FILTER_FIELD_BY_WIRE,
    FILTER_WIRE_KEYS,
    type GatedFilterKey,
    type BoardColumnDefaults,
    type BoardFilters,
    type BoardSchema,
    type FilterFieldSpec,
    type MetricMode,
    type ColumnInfo,
} from './boardTypes';

// The two columns that carry a trader group. Discovery columns ('new',
// 'active') have none.
export const CONVICTION_COLUMNS = ['insider', 'all_traders'] as const;

// The schema is the only blocking fetch on mount; it changes on deploy, not on
// the poll tick, so it sits out the board's minute cadence.
// `enabled` exists for callers outside the board — the muted-topics hook runs in
// the global header, and must not put a board request on every page.
export function useSchema(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: boardKeys.schema(),
        queryFn: ({ signal }) => fetchBoardSchema(signal),
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
        refetchOnWindowFocus: false,
        enabled: options?.enabled ?? true,
    });
}

const EMPTY_DEFAULTS: BoardColumnDefaults = {
    group: null,
    sort_by: '',
    event_category: null,
    polymarket_tag: null,
};

const NO_FIELDS: FilterFieldSpec[] = [];
const NO_WINDOWS: Record<string, string[]> = {};
const NO_THRESHOLDS: Record<string, number> = {};

export function columnDefaults(
    schema: BoardSchema | undefined,
    column: string,
): BoardColumnDefaults {
    return schema?.column_defaults?.[column] ?? EMPTY_DEFAULTS;
}

export function sortOptions(schema: BoardSchema | undefined, column: string): string[] {
    return schema?.sort_options?.[column] ?? [];
}

export function filterFields(
    schema: BoardSchema | undefined,
    column: string,
): FilterFieldSpec[] {
    return schema?.filter_spec?.[column] ?? NO_FIELDS;
}

// The wire keys this column will accept. Anything outside this set is dropped
// before the request, because the backend answers 400 for a parameter the
// column does not declare. One list drives the panel, the request and the seed.
export function columnFilterKeys(
    schema: BoardSchema | undefined,
    column: string,
): string[] {
    return filterFields(schema, column)
        .map(f => f.key)
        .filter(k => k in FILTER_FIELD_BY_WIRE);
}

export function activityWindows(schema: BoardSchema | undefined): Record<string, string[]> {
    return schema?.activity_windows ?? NO_WINDOWS;
}

export function thresholdDefaults(
    schema: BoardSchema | undefined,
    mode: string,
): Record<string, number> {
    return schema?.thresholds_by_mode?.[mode] ?? NO_THRESHOLDS;
}

export function fieldSpec(
    schema: BoardSchema | undefined,
    column: string,
    key: string,
): FilterFieldSpec | undefined {
    return filterFields(schema, column).find(f => f.key === key);
}

// Wire-keyed view of the filter state. `enabled_when` in filter_spec is written
// in backend key names, and sort_by is one of the keys it tests against.
export function filterWireValues(filters: BoardFilters): Record<string, unknown> {
    const out: Record<string, unknown> = { sort_by: filters.sortBy };
    for (const field of GATED_FIELDS) {
        out[FILTER_WIRE_KEYS[field]] = filters[field];
    }
    return out;
}

export function isFieldEnabled(
    spec: FilterFieldSpec,
    values: Record<string, unknown>,
): boolean {
    if (!spec.enabled_when) return true;
    for (const [key, want] of Object.entries(spec.enabled_when)) {
        const have = values[key];
        const ok = Array.isArray(want) ? want.includes(have as string) : want === have;
        if (!ok) return false;
    }
    return true;
}

export function fieldUnit(
    spec: FilterFieldSpec,
    values: Record<string, unknown>,
): string {
    if (spec.unit_by && spec.units) {
        return spec.units[values[spec.unit_by] as string] ?? '';
    }
    return spec.unit ?? '';
}

export function fieldLabel(spec: FilterFieldSpec, option: string): string {
    const i = spec.options?.indexOf(option) ?? -1;
    if (i >= 0 && spec.labels && spec.labels[i]) return spec.labels[i];
    return option;
}

// Re-seeding on a metric_mode switch: 2 pp and 25 % are the same strictness in
// different units, so a threshold the user never touched has to move with the
// mode rather than stay at a number that now means something else. `keep` is
// the set of wire keys the user has edited.
export function modeThresholdPatch(
    schema: BoardSchema | undefined,
    column: string,
    mode: MetricMode,
    keep?: ReadonlySet<string>,
): Partial<BoardFilters> {
    const patch: Record<string, unknown> = {};
    for (const spec of filterFields(schema, column)) {
        if (keep?.has(spec.key)) continue;
        const next = spec.defaults_by?.[mode];
        if (next === undefined) continue;
        const field = FILTER_FIELD_BY_WIRE[spec.key];
        if (field) patch[field] = next;
    }
    return patch as Partial<BoardFilters>;
}

// A field the column does not declare stays `undefined`, which is what keeps it
// off the wire. This is the seed side of the same gate the request uses.
function gatedSeed(
    schema: BoardSchema | undefined,
    column: string,
    d: BoardColumnDefaults,
): Partial<BoardFilters> {
    const declared = new Set(columnFilterKeys(schema, column));
    const seed: Record<string, unknown> = {};
    for (const field of GATED_FIELDS) {
        const wire = FILTER_WIRE_KEYS[field];
        if (!declared.has(wire)) continue;
        const value = d[wire];
        if (value !== undefined) seed[field] = value;
    }
    return seed as Partial<BoardFilters>;
}

export function defaultFilters(
    schema: BoardSchema | undefined,
    column: string,
): BoardFilters {
    const d = columnDefaults(schema, column);
    return {
        group: d.group,
        sortBy: d.sort_by,
        sortDir: 'desc',
        eventsInclude: d.event_category,
        eventsExclude: null,
        tagsInclude: d.polymarket_tag,
        tagsExclude: null,
        tagMatch: 'any',
        ...gatedSeed(schema, column, d),
    };
}

export function filterFieldOf(wireKey: string): GatedFilterKey | undefined {
    return FILTER_FIELD_BY_WIRE[wireKey];
}

const NO_STATS_WINDOWS: string[] = [];

// The two groups a card shows scores for by default. This used to be the
// hardcoded DEFAULT_SCORE_GROUPS; it is exactly the groups the two conviction
// columns already default to, so the schema can answer it and no group needs
// naming here. Deduped, because both columns may default to the same group.
export function scoreGroups(schema: BoardSchema | undefined): string[] {
    const groups = CONVICTION_COLUMNS.map(c => columnDefaults(schema, c).group).filter(
        (g): g is string => typeof g === 'string' && g.length > 0,
    );
    return [...new Set(groups)];
}

export function columnInfo(
    schema: BoardSchema | undefined,
    column: string,
): ColumnInfo | null {
    return schema?.column_info?.[column] ?? null;
}

export function sectionHelp(
    schema: BoardSchema | undefined,
    key: string,
): string | undefined {
    return schema?.section_help?.[key];
}

export function fieldHelp(spec: FilterFieldSpec | undefined): string | undefined {
    return spec?.help;
}

export function statsWindows(schema: BoardSchema | undefined): string[] {
    return schema?.stats_windows ?? NO_STATS_WINDOWS;
}